import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Users, Store, Handshake, Eye, LogOut, TrendingUp, Wallet, Check, Star, LayoutDashboard, Activity,
  CreditCard, Receipt, Settings, BadgeCheck, Menu, ChevronRight, Mail, MapPin, KeyRound, ShieldCheck, ExternalLink, FileText, Download, ScrollText,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { fmt, euro } from "../../lib/format";
import { useI18n } from "../../lib/i18n";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/Logo";
import { LanguageToggle } from "../../components/LanguageToggle";
import { RichTextEditor } from "../../components/RichTextEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

function KpiCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="rounded-2xl border border-cardline bg-card2/60 p-4">
      <div className={`grid h-10 w-10 place-items-center rounded-xl ${color}`}><Icon size={20} /></div>
      <div className="mt-3 font-heading text-2xl font-extrabold">{value}</div>
      <div className="text-xs text-slate-400">{label}</div>
      {sub && <div className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-emerald-400"><TrendingUp size={12} /> {sub}</div>}
    </div>
  );
}

function Overview() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/users", { params: { role: "creator" } })
      .then(({ data }) => setPendingUsers(data.users.filter((u) => !u.verified))).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const verify = async (id) => { await api.post(`/admin/users/${id}/verify`); toast.success(t("verified")); load(); };
  if (!stats) return <div className="py-20 text-center text-slate-500">{t("loading")}</div>;
  const k = stats.kpis;
  const donut = [
    { name: t("kpiCreators"), value: stats.distribution.creators, color: "#4f6fd6" },
    { name: t("kpiBusinesses"), value: stats.distribution.commerce, color: "#D9A441" },
  ];
  const total = stats.distribution.creators + stats.distribution.commerce;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard icon={Users} label={t("kpiCreators")} value={fmt(k.creators)} sub="+12,6%" color="bg-indigo-500/20 text-indigo-300" />
        <KpiCard icon={Store} label={t("kpiBusinesses")} value={fmt(k.commerce)} sub="+15,3%" color="bg-gold/20 text-gold" />
        <KpiCard icon={Handshake} label={t("kpiCollabs")} value={fmt(k.collaborations)} sub={t("inProgress")} color="bg-emerald-500/20 text-emerald-300" />
        <KpiCard icon={Eye} label={t("kpiViews")} value={fmt(k.views)} sub="+22,1%" color="bg-sky-500/20 text-sky-300" />
        <KpiCard icon={ScrollText} label={t("signedContracts")} value={fmt(k.contracts || 0)} color="bg-fuchsia-500/20 text-fuchsia-300" />
        <KpiCard icon={FileText} label={t("deliverables")} value={fmt(k.deliverables || 0)} color="bg-amber-500/20 text-amber-300" />
        <KpiCard icon={Handshake} label={t("activeCampaigns")} value={fmt(k.open_campaigns || 0)} color="bg-teal-500/20 text-teal-300" />
        <KpiCard icon={Wallet} label={t("totalRevenue")} value={euro(k.revenue || 0)} color="bg-emerald-500/20 text-emerald-300" />
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        <div className="rounded-2xl border border-cardline bg-card2/60 p-4 lg:col-span-2">
          <h3 className="mb-3 font-heading text-sm font-semibold">{t("signupEvolution")}</h3>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={stats.evolution} margin={{ left: -20, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#213665" />
              <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0F1D3D", border: "1px solid #213665", borderRadius: 12, color: "#fff" }} />
              <Line type="monotone" dataKey="creators" name={t("kpiCreators")} stroke="#4f6fd6" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="commerce" name={t("kpiBusinesses")} stroke="#D9A441" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl border border-cardline bg-card2/60 p-4">
          <h3 className="mb-2 font-heading text-sm font-semibold">{t("userDistribution")}</h3>
          <div className="relative">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={donut} dataKey="value" innerRadius={55} outerRadius={78} paddingAngle={2}>
                  {donut.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-x-0 top-[62px] flex flex-col items-center">
              <span className="font-heading text-xl font-extrabold">{fmt(total)}</span>
              <span className="text-[10px] text-slate-400">{t("totalUsers")}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-cardline bg-card2/60 p-4">
        <h3 className="mb-3 font-heading text-sm font-semibold">{t("pendingRequests")}</h3>
        {pendingUsers.length === 0 && <p className="py-6 text-center text-sm text-slate-500">—</p>}
        <div className="grid gap-2 sm:grid-cols-2">
          {pendingUsers.map((u) => (
            <div key={u.id} data-testid={`pending-user-${u.id}`} className="flex items-center gap-3 rounded-xl bg-ink2/50 p-2.5">
              <img src={u.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
              <div className="flex-1"><div className="text-sm font-semibold">{u.name}</div>
                <div className="text-[11px] text-slate-400">{u.categories?.slice(0, 2).join(" · ")}</div></div>
              <button onClick={() => verify(u.id)} data-testid={`verify-user-${u.id}`}
                className="flex items-center gap-1 rounded-lg bg-emerald-500/20 px-2.5 py-1.5 text-xs font-semibold text-emerald-400">
                <Check size={14} /> {t("verify")}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Table({ headers, children }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-cardline bg-card2/60">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-cardline text-xs uppercase text-slate-400">
          <tr>{headers.map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-cardline/60">{children}</tbody>
      </table>
    </div>
  );
}

function Establishments() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/establishments").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <div className="py-20 text-center text-slate-500">{t("loading")}</div>;
  const rows = [...data.commerces];
  return (
    <Table headers={[t("kpiBusinesses"), t("email"), t("campaignsCount"), t("status"), ""]}>
      {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">{t("noData")}</td></tr>}
      {rows.map((u) => (
        <tr key={u.id} data-testid={`establishment-${u.id}`} onClick={() => navigate(`/admin/member/${u.id}`)} className="cursor-pointer transition-colors hover:bg-ink2/40">
          <td className="px-4 py-3"><div className="flex items-center gap-2"><img src={u.avatar} alt="" className="h-8 w-8 rounded-full object-cover" /> {u.name}</div></td>
          <td className="px-4 py-3 text-slate-400">{u.email}</td>
          <td className="px-4 py-3">{u.campaigns}</td>
          <td className="px-4 py-3">{u.verified ? <span className="inline-flex items-center gap-1 text-emerald-400"><BadgeCheck size={14} /> {t("verified")}</span> : <span className="text-amber-400">{t("pending")}</span>}</td>
          <td className="px-4 py-3"><ChevronRight size={16} className="text-slate-500" /></td>
        </tr>
      ))}
    </Table>
  );
}

function Creators() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/creators").then(({ data }) => setItems(data.creators)).catch(() => {}); }, []);
  if (!items) return <div className="py-20 text-center text-slate-500">{t("loading")}</div>;
  return (
    <Table headers={[t("kpiCreators"), t("followers"), t("navSubscriptions"), t("status"), ""]}>
      {items.map((u) => (
        <tr key={u.id} data-testid={`admin-creator-${u.id}`} onClick={() => navigate(`/admin/member/${u.id}`)} className="cursor-pointer transition-colors hover:bg-ink2/40">
          <td className="px-4 py-3"><div className="flex items-center gap-2"><img src={u.avatar} alt="" className="h-8 w-8 rounded-full object-cover" /> {u.name}</div></td>
          <td className="px-4 py-3 text-slate-400">{fmt(u.followers || 0)}</td>
          <td className="px-4 py-3">{u.subscription_active ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/10 px-2 py-0.5 text-xs text-emerald-400"><Star size={11} /> {t("premiumCreator")}</span> : <span className="text-slate-500">—</span>}</td>
          <td className="px-4 py-3">{u.verified ? <span className="text-emerald-400">{t("verified")}</span> : <span className="text-amber-400">{t("pending")}</span>}</td>
          <td className="px-4 py-3"><ChevronRight size={16} className="text-slate-500" /></td>
        </tr>
      ))}
    </Table>
  );
}

function Transactions({ kind, testid }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/transactions", { params: kind ? { kind } : {} }).then(({ data }) => setData(data)).catch(() => {}); }, [kind]);
  if (!data) return <div className="py-20 text-center text-slate-500">{t("loading")}</div>;
  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <KpiCard icon={Wallet} label={t("totalRevenue")} value={euro(data.total_paid)} color="bg-emerald-500/20 text-emerald-300" />
        <KpiCard icon={CreditCard} label={t("activeSubscriptions")} value={fmt(data.active_subscriptions)} color="bg-fuchsia-500/20 text-fuchsia-300" />
        <KpiCard icon={Receipt} label={t("licenseOrders")} value={fmt(data.transactions.length)} color="bg-gold/20 text-gold" />
      </div>
      <Table headers={[t("date"), t("kpiCreators"), t("amount"), t("status")]}>
        {data.transactions.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">{t("noData")}</td></tr>}
        {data.transactions.map((x, i) => (
          <tr key={i} onClick={() => x.session_id && navigate(`/admin/invoice/${x.session_id}`)} data-testid={`tx-row-${i}`} className="cursor-pointer hover:bg-ink2/40">
            <td className="px-4 py-3 text-slate-400">{(x.created_at || "").slice(0, 10)}</td>
            <td className="px-4 py-3">{x.creator_name || "—"}</td>
            <td className="px-4 py-3 font-semibold text-gold">{euro(x.amount)}</td>
            <td className="px-4 py-3">
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${x.payment_status === "paid" ? "bg-emerald-400/10 text-emerald-400" : "bg-amber-400/10 text-amber-400"}`}>{x.payment_status}</span>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function Traffic() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/traffic").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <div className="py-20 text-center text-slate-500">{t("loading")}</div>;
  return (
    <div className="space-y-4">
      <KpiCard icon={Eye} label={t("totalViews")} value={fmt(data.total)} sub={t("last14days")} color="bg-sky-500/20 text-sky-300" />
      <div className="rounded-2xl border border-cardline bg-card2/60 p-4">
        <h3 className="mb-3 font-heading text-sm font-semibold">{t("webTraffic")} · {t("last14days")}</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.daily} margin={{ left: -20, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#213665" />
            <XAxis dataKey="date" stroke="#64748B" fontSize={10} tickFormatter={(d) => (d || "").slice(5)} />
            <YAxis stroke="#64748B" fontSize={11} />
            <Tooltip contentStyle={{ background: "#0F1D3D", border: "1px solid #213665", borderRadius: 12, color: "#fff" }} />
            <Bar dataKey="views" fill="#D9A441" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function StripePanel() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stripe-status").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return null;
  const Row = ({ label, value, ok }) => (
    <div className="flex items-center justify-between border-b border-cardline/60 py-2 text-sm last:border-0">
      <span className="text-slate-400">{label}</span>
      <span className={`font-mono text-xs ${ok === false ? "text-red-400" : "text-slate-200"}`}>{value}</span>
    </div>
  );
  return (
    <div className="max-w-lg space-y-1 rounded-2xl border border-cardline bg-card2/60 p-5" data-testid="admin-stripe">
      <div className="mb-2 flex items-center gap-2">
        <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#635bff]/20 text-[#a5a0ff]"><KeyRound size={18} /></div>
        <h3 className="font-heading text-lg font-semibold">Stripe</h3>
        <span className={`ml-auto rounded-full px-2.5 py-0.5 text-xs font-bold ${s.mode === "live" ? "bg-emerald-400/15 text-emerald-400" : "bg-amber-400/15 text-amber-400"}`}>{s.mode?.toUpperCase()}</span>
      </div>
      <Row label="Account ID" value={s.account_id || "—"} />
      <Row label="Publishable key" value={s.publishable_key || "—"} />
      <Row label="Secret key" value={s.secret_configured ? "•••••• ✓" : "non configurée"} ok={s.secret_configured} />
      <Row label="Webhook" value={s.webhook_configured ? "configuré ✓" : "manquant"} ok={s.webhook_configured} />
      <Row label="Devise" value={(s.currency || "eur").toUpperCase()} />
      <Row label={t("commission")} value={`${s.commission_pct}%`} />
      <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-cardline py-2.5 text-sm font-semibold text-slate-200 hover:border-gold hover:text-gold">
        <ExternalLink size={14} /> Dashboard Stripe
      </a>
      <p className="pt-2 text-[11px] text-slate-500">Les clés Stripe sont gérées de façon sécurisée (variables d'environnement) et passent automatiquement en mode live après validation du compte.</p>
    </div>
  );
}

function Config() {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  useEffect(() => { api.get("/admin/config").then(({ data }) => setCfg(data.config)).catch(() => {}); }, []);
  if (!cfg) return <div className="py-20 text-center text-slate-500">{t("loading")}</div>;
  const set = (k) => (e) => setCfg((c) => ({ ...c, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/config", {
        subscription_amount: Number(cfg.subscription_amount),
        commission_pct: Number(cfg.commission_pct),
        platform_name: cfg.platform_name,
        primary_color: cfg.primary_color,
        contract_template: cfg.contract_template,
      });
      setCfg(data.config);
      toast.success(t("configSaved"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const resetContract = async () => {
    try {
      const { data } = await api.get("/admin/config/contract-default");
      setCfg((c) => ({ ...c, contract_template: data.contract_template }));
      setEditorKey((k) => k + 1);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const SAMPLE = {
    business_name: "Le Petit Bistrot", creator_name: "Camille Dupont",
    campaign_title: "Menu d'automne", city: "Lyon",
    reward: "Repas offert + 50€", budget: "50", date: new Date().toISOString().slice(0, 10),
  };
  const previewHtml = Object.entries(SAMPLE).reduce(
    (out, [k, v]) => out.split(`{{${k}}}`).join(v), cfg.contract_template || "");
  const field = (label, key, type = "text") => (
    <div>
      <label className="mb-1 block text-xs font-semibold text-slate-400">{label}</label>
      <input type={type} value={cfg[key] ?? ""} onChange={set(key)} data-testid={`config-${key}`}
        className="w-full rounded-xl border border-cardline bg-ink2/60 px-4 py-3 text-sm outline-none focus:border-gold" />
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4 rounded-2xl border border-cardline bg-card2/60 p-5" data-testid="admin-config">
          <h3 className="font-heading text-lg font-semibold">{t("appConfig")}</h3>
          {field(t("platformName"), "platform_name")}
          <div className="grid grid-cols-2 gap-3">
            {field(t("monthlyPrice"), "subscription_amount", "number")}
            {field(t("commission"), "commission_pct", "number")}
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-400">{t("primaryColor")}</label>
            <div className="flex items-center gap-3">
              <input type="color" value={cfg.primary_color || "#D9A441"} onChange={set("primary_color")} data-testid="config-primary_color" className="h-11 w-16 cursor-pointer rounded-lg border border-cardline bg-ink2/60" />
              <span className="text-sm text-slate-300">{cfg.primary_color}</span>
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="config-save-btn" className="w-full rounded-xl bg-gold py-3 font-bold text-ink disabled:opacity-60">
            {saving ? t("loading") : t("saveConfig")}
          </button>
        </div>
        <StripePanel />
      </div>

      <div className="rounded-2xl border border-cardline bg-card2/60 p-5" data-testid="admin-contract-config">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading text-lg font-semibold">{t("contractTemplate")}</h3>
          <button onClick={resetContract} data-testid="contract-reset-btn"
            className="rounded-lg border border-cardline px-3 py-1.5 text-xs font-semibold text-slate-300 hover:border-gold hover:text-gold">
            {t("resetDefault")}
          </button>
        </div>
        <p className="mb-3 text-xs text-slate-400">{t("contractTemplateHint")}</p>
        <div className="grid gap-4 lg:grid-cols-2">
          <RichTextEditor key={editorKey} value={cfg.contract_template}
            onChange={(v) => setCfg((c) => ({ ...c, contract_template: v }))} insertLabel={t("insertTag")} />
          <div>
            <label className="mb-2 block text-xs font-semibold text-slate-400">{t("contractPreview")}</label>
            <div className="contract-prose min-h-[220px] max-h-[360px] overflow-y-auto rounded-xl border border-cardline bg-ink2/40 px-4 py-3 text-sm leading-relaxed text-slate-200"
              data-testid="contract-preview" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="contract-save-btn" className="mt-4 w-full rounded-xl bg-gold py-3 font-bold text-ink disabled:opacity-60">
          {saving ? t("loading") : t("saveConfig")}
        </button>
      </div>
    </div>
  );
}

function Contracts() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/contracts").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <div className="py-20 text-center text-slate-500">{t("loading")}</div>;
  const openPdf = (c) => {
    const token = localStorage.getItem("lc_token") || "";
    window.open(`${api.defaults.baseURL}/campaigns/${c.campaign_id}/contract/pdf?auth=${token}&creator_id=${c.creator_id}`, "_blank");
  };
  return (
    <div className="space-y-4" data-testid="admin-contracts">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <KpiCard icon={ScrollText} label={t("signedContracts")} value={fmt(data.contracts.length)} color="bg-fuchsia-500/20 text-fuchsia-300" />
        <KpiCard icon={FileText} label={t("deliverables")} value={fmt(data.deliverables_total)} color="bg-amber-500/20 text-amber-300" />
      </div>
      <Table headers={[t("date"), t("navContracts"), t("kpiCreators"), t("kpiBusinesses"), ""]}>
        {data.contracts.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">{t("noContracts")}</td></tr>}
        {data.contracts.map((c, i) => (
          <tr key={i} data-testid={`contract-row-${i}`} className="hover:bg-ink2/40">
            <td className="px-4 py-3 text-slate-400">{(c.accepted_at || "").slice(0, 10)}</td>
            <td className="px-4 py-3">{c.campaign_title || "—"}</td>
            <td className="px-4 py-3">{c.creator_name || "—"}</td>
            <td className="px-4 py-3 text-slate-400">{c.business_name || "—"}</td>
            <td className="px-4 py-3">
              <button onClick={() => openPdf(c)} data-testid={`contract-pdf-${i}`}
                className="flex items-center gap-1 rounded-lg border border-cardline px-2.5 py-1.5 text-xs font-semibold text-slate-300 hover:border-gold hover:text-gold">
                <Download size={13} /> PDF
              </button>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

const SECTIONS = [
  { key: "overview", icon: LayoutDashboard, labelKey: "navOverview", el: <Overview /> },
  { key: "establishments", icon: Store, labelKey: "navEstablishments", el: <Establishments /> },
  { key: "creators", icon: Users, labelKey: "navCreators", el: <Creators /> },
  { key: "contracts", icon: ScrollText, labelKey: "navContracts", el: <Contracts /> },
  { key: "subscriptions", icon: CreditCard, labelKey: "navSubscriptions", el: <Transactions kind="subscription" testid="admin-subscriptions" /> },
  { key: "billing", icon: Receipt, labelKey: "navBilling", el: <Transactions testid="admin-billing" /> },
  { key: "traffic", icon: Activity, labelKey: "navTraffic", el: <Traffic /> },
  { key: "config", icon: Settings, labelKey: "navConfig", el: <Config /> },
];

export default function AdminDashboard() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const current = SECTIONS.find((s) => s.key === active);
  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-ink text-white">
      <div className="pointer-events-none fixed inset-0 grain opacity-40" />
      <header className="sticky top-0 z-40 glass border-b border-cardline px-4 py-3">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={30} />
            <span className="hidden items-center gap-1.5 rounded-full bg-gold/15 px-2.5 py-1 text-xs font-semibold text-gold sm:flex">
              <LayoutDashboard size={13} /> {t("adminBackoffice")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <button onClick={handleLogout} data-testid="admin-logout-btn"
              className="grid h-9 w-9 place-items-center rounded-full border border-cardline bg-ink2/60 text-slate-300 hover:text-gold"><LogOut size={17} /></button>
            <img src={user?.avatar} alt="" className="h-9 w-9 rounded-full border border-gold/50 object-cover" />
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-6 px-4 py-6">
        {/* Sidebar */}
        <aside className="sticky top-20 hidden h-fit w-56 shrink-0 space-y-1 lg:block" data-testid="admin-sidebar">
          {SECTIONS.map((s) => (
            <button key={s.key} onClick={() => setActive(s.key)} data-testid={`admin-nav-${s.key}`}
              className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${active === s.key ? "bg-gold text-ink" : "text-slate-300 hover:bg-card2/60"}`}>
              <s.icon size={18} /> {t(s.labelKey)}
            </button>
          ))}
        </aside>

        {/* Mobile nav */}
        <div className="min-w-0 flex-1">
          <div className="mb-4 flex items-center gap-2 lg:hidden">
            <Menu size={16} className="text-slate-400" />
            <div className="no-scrollbar flex gap-2 overflow-x-auto">
              {SECTIONS.map((s) => (
                <button key={s.key} onClick={() => setActive(s.key)} data-testid={`admin-mnav-${s.key}`}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${active === s.key ? "bg-gold text-ink" : "bg-card2/60 text-slate-300"}`}>
                  {t(s.labelKey)}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <h1 className="font-heading text-2xl font-extrabold">{t(current.labelKey)}</h1>
            {active === "overview" && <p className="mt-1 text-sm text-slate-400">{t("welcomeAdmin")}, {user?.name?.split(" ")[0]} — {t("adminOverview")}</p>}
          </div>

          <div data-testid={`admin-section-${active}`}>{current.el}</div>
        </div>
      </div>
    </div>
  );
}
